import { useQuery } from '@apollo/client';
import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { DealStatusEnumType } from '__generated__/graphql';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanelWithReadMode } from 'components/FormPanel';

import LoadingIndicator from 'primitives/LoadingIndicator';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

const ADD_DEAL_ENGAGEMENT_RULES_MUTATION = gql(`
  mutation AddDealEngagementRules($dealId: ID!, $customEngagementRules: [String!]!) {
    addDealEngagementRules(dealId: $dealId, customEngagementRules: $customEngagementRules) {
      id
      customEngagementRules
    }
  }
`);

const GET_DEAL_ENGAGEMENT_RULES_QUERY = gql(`
  query GetDealEngagementRules($dealId: ID!) {
    deal(id: $dealId) {
      id
      customEngagementRules
    }
  }
`);

const DISCLOSURES_QUERY = gql(`
  query DealDisclosures($id: ID!) {
    deal(id: $id) {
      id
      status
      dealDisclosures {
        id
        risksNote
        hasLeadInvestedInCompanyBefore
        doesLeadHaveAdvisoryShares
        isLeadEmployeeAtCompany
        doesLeadHaveRelativesInCompany
        hasSameTermsForCoInvestors
        isLeadInvestingOnSameTerms
        isLeadInvestingFromFund
        otherDisclosuresNote
      }
      syndicate {
        id
        type
      }
    }
  }
`);

const UPDATE_DEAL_DISCLOSURES_MUTATION = gql(`
  mutation UpdateDealDisclosures(
    $dealId: ID!
    $risksNote: String
    $hasLeadInvestedInCompanyBefore: Boolean!
    $doesLeadHaveAdvisoryShares: Boolean!
    $isLeadEmployeeAtCompany: Boolean!
    $doesLeadHaveRelativesInCompany: Boolean!
    $hasSameTermsForCoInvestors: Boolean!
    $isLeadInvestingOnSameTerms: Boolean!
    $isLeadInvestingFromFund: Boolean!
    $otherDisclosuresNote: String
  ) {
    updateDealDisclosures(
      dealId: $dealId
      risksNote: $risksNote
      hasLeadInvestedInCompanyBefore: $hasLeadInvestedInCompanyBefore
      doesLeadHaveAdvisoryShares: $doesLeadHaveAdvisoryShares
      isLeadEmployeeAtCompany: $isLeadEmployeeAtCompany
      doesLeadHaveRelativesInCompany: $doesLeadHaveRelativesInCompany
      hasSameTermsForCoInvestors: $hasSameTermsForCoInvestors
      isLeadInvestingOnSameTerms: $isLeadInvestingOnSameTerms
      isLeadInvestingFromFund: $isLeadInvestingFromFund
      otherDisclosuresNote: $otherDisclosuresNote
    ) {
      id
      dealDisclosures {
        id
        risksNote
        hasLeadInvestedInCompanyBefore
        doesLeadHaveAdvisoryShares
        isLeadEmployeeAtCompany
        doesLeadHaveRelativesInCompany
        hasSameTermsForCoInvestors
        isLeadInvestingOnSameTerms
        isLeadInvestingFromFund
        otherDisclosuresNote
      }
    }
  }
`);

const ENGAGEMENT_RULE_OTHERS_VALUE = 'others';

const RisksDisclosures: FC<{ dealStatus: DealStatusEnumType }> = ({ dealStatus }) => {
  const { showToast, handleToastOpen, handleToastClose } = useToast();
  const { dealId } = useParams<{ dealId: string }>() as {
    dealId: string;
  };

  const { loading, error, data, refetch } = useQuery(DISCLOSURES_QUERY, {
    variables: {
      id: dealId,
    },
  });

  const {
    loading: engagementLoading,
    data: engagementRulesData,
    refetch: refetchEngagementRules,
  } = useQuery(GET_DEAL_ENGAGEMENT_RULES_QUERY, {
    variables: { dealId },
    skip: !dealId,
  });

  const [updateDealDisclosures, { loading: updateLoading, error: updateError }] = useMutation(
    UPDATE_DEAL_DISCLOSURES_MUTATION
  );

  const [addDealEngagementRules, { loading: addEngagementLoading, error: addEngagementError }] =
    useMutation(ADD_DEAL_ENGAGEMENT_RULES_MUTATION);

  if (loading || engagementLoading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  function disableEdit() {
    return [
      DealStatusEnumType.Cancelled,
      DealStatusEnumType.Closed,
      DealStatusEnumType.Deleted,
      DealStatusEnumType.Exited,
      DealStatusEnumType.Wired,
    ].includes(dealStatus);
  }

  const defaultEngagementRules = (engagementRulesData?.deal?.customEngagementRules ?? []).filter(
    (r): r is string => r != null && r !== ''
  );
  const defaultEnableCustomEngagementRules = defaultEngagementRules.length > 0;

  return (
    <div>
      <FormPanelWithReadMode
        loading={updateLoading || addEngagementLoading}
        error={updateError || addEngagementError}
        onSubmit={formData => {
          Promise.all([
            addDealEngagementRules({
              variables: {
                dealId,
                customEngagementRules: formData?.enableCustomEngagementRules
                  ? formData.customEngagementRules
                  : [],
              },
            }),
            updateDealDisclosures({
              variables: {
                dealId,
                risksNote: formData.risksNote,
                hasLeadInvestedInCompanyBefore: formData.hasLeadInvestedInCompanyBefore,
                doesLeadHaveAdvisoryShares: formData.doesLeadHaveAdvisoryShares,
                isLeadEmployeeAtCompany: formData.isLeadEmployeeAtCompany,
                doesLeadHaveRelativesInCompany: formData.doesLeadHaveRelativesInCompany,
                hasSameTermsForCoInvestors: formData.hasSameTermsForCoInvestors,
                isLeadInvestingOnSameTerms: formData.isLeadInvestingOnSameTerms,
                isLeadInvestingFromFund: formData.isLeadInvestingFromFund,
                otherDisclosuresNote: formData.otherPotentialConflicts
                  ? formData.otherDisclosures
                  : undefined,
              },
            }),
          ]).then(() => {
            refetch();
            refetchEngagementRules();
          });
        }}
        disableEdit={disableEdit()}
      >
        <FormInput
          fieldName="risksNote"
          type="textarea"
          fullWidth
          defaultValue={data.deal.dealDisclosures?.risksNote || ''}
          helperText="Disclosing specific risk factors is required for compliance"
          label="Risks Note *"
          validators={{
            required: true,
          }}
        />
        <FormInput
          type="switch"
          fieldName="hasLeadInvestedInCompanyBefore"
          label="The lead has invested in a previous round"
          defaultValue={data.deal.dealDisclosures?.hasLeadInvestedInCompanyBefore || false}
        />
        <FormInput
          type="switch"
          fieldName="doesLeadHaveAdvisoryShares"
          label="I have advisory shares, warrants, or other shares"
          defaultValue={data.deal.dealDisclosures?.doesLeadHaveAdvisoryShares || false}
        />
        <FormInput
          type="switch"
          fieldName="isLeadEmployeeAtCompany"
          label="I am an officer or employee of the company"
          defaultValue={data.deal.dealDisclosures?.isLeadEmployeeAtCompany || false}
        />
        <FormInput
          type="switch"
          fieldName="doesLeadHaveRelativesInCompany"
          label="I have a relative or significant other working at the company"
          defaultValue={data.deal.dealDisclosures?.doesLeadHaveRelativesInCompany || false}
        />
        <FormInput
          type="switch"
          fieldName="hasSameTermsForCoInvestors"
          label="I have the same terms as other co-investors"
          defaultValue={data.deal.dealDisclosures?.hasSameTermsForCoInvestors || false}
        />
        <FormInput
          type="switch"
          fieldName="isLeadInvestingOnSameTerms"
          label="I am investing on the same terms as other investors"
          defaultValue={data.deal.dealDisclosures?.isLeadInvestingOnSameTerms || false}
        />
        <FormInput
          type="switch"
          fieldName="isLeadInvestingFromFund"
          label="I am investing from a fund"
          defaultValue={data.deal.dealDisclosures?.isLeadInvestingFromFund || false}
        />
        <FormInput
          type="switch"
          fieldName="otherPotentialConflicts"
          label="I have other potential conflicts to disclose"
          defaultValue={data.deal.dealDisclosures?.otherDisclosuresNote ? true : false}
        />
        <FormInput
          type="textarea"
          fullWidth
          fieldName="otherDisclosures"
          label="Other Disclosures"
          defaultValue={data.deal.dealDisclosures?.otherDisclosuresNote || ''}
          conditionsToShow={{
            matches: [
              {
                field: 'otherPotentialConflicts',
                condition: '===',
                value: 'true',
              },
            ],
          }}
        />

        <div className="col-span-full mt-4">
          <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
            <FormInput
              type="switch"
              fieldName="enableCustomEngagementRules"
              label="Disclose deal-specific restrictions (if any)"
              helperText="Select conditions to determine how this investment opportunity is accessed"
              defaultValue={defaultEnableCustomEngagementRules}
            />
            <FormInput
              type="filter-multi-select"
              fieldName="customEngagementRules"
              label="Select condition"
              placeholder="Select"
              defaultValue={defaultEngagementRules}
              options={[
                {
                  value:
                    'I/We am/are not presently employed at or have an existing stake in a direct or closely competing business',
                  label:
                    'Investors must not be currently employed at or having an existing stake in a direct or closely competing business',
                },
                {
                  value:
                    'I/We confirm that we have previously invested in or have worked in/with companies in the same sector',
                  label:
                    'Limit participation to investors with prior or current sector-specific/domain experience or exposure',
                },
                { value: ENGAGEMENT_RULE_OTHERS_VALUE, label: 'Others' },
              ]}
              conditionsToShow={{
                matches: [
                  {
                    field: 'enableCustomEngagementRules',
                    condition: '===',
                    value: 'true',
                  },
                ],
              }}
              onOthersPick={handleToastOpen}
            />
          </div>
        </div>
      </FormPanelWithReadMode>
      <Toast
        title="Notice"
        message="Please contact your deal manager at hi@angellistindia.com"
        show={showToast}
        handleClose={handleToastClose}
        type="error"
        // TODO:  MOVE TO BOTTOM RIGHT
        position="bottom-right"
      />
    </div>
  );
};

export default RisksDisclosures;
